/**
 * Black Market state (`backend/src/game/market/`), the shop window's third
 * face. Same `createStore` + fetch shape as `shop-state.ts`. The wallet is read
 * from `gameStateStore` (kept live by the HUD's `economy:updated`), not
 * duplicated here; every action writes the fresh inventory sheet back.
 */
import { createStore } from '../../core/state/store';
import { api, ApiError } from '../../core/network/api';
import { gameStateStore } from './game-state';
import type { InventorySheet } from './game-state.types';
import type {
  MarketCategory, MarketListing, MarketPage, MarketSale, MarketSellable, MarketSort, MarketState, MarketView,
} from './market-state.types';

/** Mirrors `MARKET_FEE_RATE` in market.config.ts — the preview must match what the server takes. */
export const MARKET_FEE_RATE = 0.05;

export const feeFor = (price: number): number => Math.ceil(price * MARKET_FEE_RATE);

const INITIAL_STATE: MarketState = {
  view: 'browse',
  category: 'ALL',
  sort: 'newest',
  page: 1,
  listings: null,
  mine: null,
  sales: null,
  sellable: null,
  sell: null,
  busy: false,
  error: null,
  notice: null,
};

export const marketStore = createStore<MarketState>({ ...INITIAL_STATE });

function msg(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  return err instanceof Error && err.message ? err.message : fallback;
}

function writeSheet(sheet: InventorySheet | undefined): void {
  if (sheet) gameStateStore.set((s) => ({ ...s, inventory: sheet }));
}

function listingsQuery(s: MarketState): string {
  const q = new URLSearchParams({ sort: s.sort, page: String(s.page) });
  if (s.category !== 'ALL') q.set('category', s.category);
  return `/market/listings?${q.toString()}`;
}

/** Loads the current view. Quiet when `quiet` (a refresh after an action keeps the grid on screen). */
export async function loadMarket(quiet = false): Promise<void> {
  const s = marketStore.get();
  if (!quiet) marketStore.set((x) => ({ ...x, busy: true, error: null }));
  try {
    if (s.view === 'browse') {
      const listings = await api.get<MarketPage<MarketListing>>(listingsQuery(s));
      marketStore.set((x) => ({ ...x, listings }));
    } else if (s.view === 'mine') {
      const mine = await api.get<MarketListing[]>('/market/mine');
      marketStore.set((x) => ({ ...x, mine }));
    } else {
      const sales = await api.get<MarketSale[]>('/market/sales');
      marketStore.set((x) => ({ ...x, sales }));
    }
  } catch (err) {
    marketStore.set((x) => ({ ...x, error: msg(err, 'The Black Market is unreachable.') }));
  } finally {
    marketStore.set((x) => ({ ...x, busy: false }));
  }
}

export function setMarketView(view: MarketView): void {
  marketStore.set((s) => ({ ...s, view, error: null, notice: null }));
  void loadMarket();
}

export function setMarketFilter(patch: { category?: MarketCategory; sort?: MarketSort; page?: number }): void {
  marketStore.set((s) => ({ ...s, ...patch, page: patch.page ?? 1, error: null }));
  void loadMarket();
}

export async function buyListing(id: string): Promise<boolean> {
  const listing = marketStore.get().listings?.items.find((l) => l.id === id);
  marketStore.set((s) => ({ ...s, busy: true, error: null, notice: null }));
  try {
    const res = await api.post<{ sheet: InventorySheet; balance: { cash: number; gold: number } }>(`/market/listings/${id}/buy`);
    writeSheet(res.sheet);
    marketStore.set((s) => ({ ...s, notice: listing ? `Bought the ${listing.name}. It's in your hideout.` : 'Bought. It’s in your hideout.' }));
    await loadMarket(true);
    return true;
  } catch (err) {
    marketStore.set((s) => ({ ...s, error: msg(err, 'That purchase did not go through.') }));
    await loadMarket(true);
    return false;
  } finally {
    marketStore.set((s) => ({ ...s, busy: false }));
  }
}

export async function cancelListing(id: string): Promise<void> {
  marketStore.set((s) => ({ ...s, busy: true, error: null, notice: null }));
  try {
    const res = await api.del<{ sheet: InventorySheet }>(`/market/listings/${id}`);
    writeSheet(res.sheet);
    marketStore.set((s) => ({ ...s, notice: 'Listing taken down. The item is back in your hideout.' }));
  } catch (err) {
    marketStore.set((s) => ({ ...s, error: msg(err, 'Could not take that listing down.') }));
  } finally {
    marketStore.set((s) => ({ ...s, busy: false }));
    await loadMarket(true);
  }
}

/** Opens the sell prompt and loads what can be listed. */
export async function openSell(): Promise<void> {
  marketStore.set((s) => ({ ...s, sell: { userItemId: null, priceGold: 0 }, sellable: null, error: null, notice: null }));
  try {
    const sellable = await api.get<MarketSellable[]>('/market/sellable');
    marketStore.set((s) => ({ ...s, sellable }));
  } catch (err) {
    marketStore.set((s) => ({ ...s, sellable: [], error: msg(err, 'Could not read your hideout.') }));
  }
}

export function closeSell(): void {
  marketStore.set((s) => ({ ...s, sell: null, sellable: null }));
}

/** Picks a unit; the price starts at the cap (what was paid). */
export function pickSellUnit(userItemId: string): void {
  const unit = marketStore.get().sellable?.find((u) => u.userItemId === userItemId);
  if (!unit) return;
  marketStore.set((s) => ({ ...s, sell: { userItemId, priceGold: unit.priceCap }, error: null }));
}

export function setSellPrice(priceGold: number): void {
  marketStore.set((s) => (s.sell ? { ...s, sell: { ...s.sell, priceGold } } : s));
}

export async function submitListing(): Promise<boolean> {
  const { sell } = marketStore.get();
  if (!sell?.userItemId) return false;
  marketStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const res = await api.post<{ listing: MarketListing; sheet: InventorySheet }>('/market/listings', {
      userItemId: sell.userItemId, priceGold: sell.priceGold,
    });
    writeSheet(res.sheet);
    marketStore.set((s) => ({
      ...s, sell: null, sellable: null, view: 'mine',
      notice: `Listed the ${res.listing.name} for ${res.listing.priceGold} Gold.`,
    }));
    await loadMarket(true);
    return true;
  } catch (err) {
    marketStore.set((s) => ({ ...s, error: msg(err, 'Could not list that item.') }));
    return false;
  } finally {
    marketStore.set((s) => ({ ...s, busy: false }));
  }
}

export function resetMarketState(): void {
  marketStore.set({ ...INITIAL_STATE });
}
